import { Navigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, type ReactNode } from 'react';

import { Spinner } from '../components/ui.tsx';
import { desktop, useDesktopSetup, useDesktopUpdate } from '../platform.ts';
import { UpdateBeforeWork } from './UpdateBeforeWork.tsx';

/**
 * Dob Desktop only (D120): until this PC is registered, every address leads to the first-run
 * wizard (`/setup`); the main process refuses the API anyway (DEVICE_NOT_REGISTERED). If the
 * registration ends while the app is open (revoked by IT), the page is reloaded on the wizard, so
 * nothing of the signed-in session stays in memory. While a start-up update runs before work (a
 * missed update window, M7-7) the update screen replaces every page. In the admin web this renders
 * its children.
 */
const AGENT_PAGES = new Set(['/backup-agent', '/updates', '/setup']);

export function DesktopGate({ children }: { readonly children: ReactNode }) {
  if (!desktop) return children;
  return <Gate>{children}</Gate>;
}

function Gate({ children }: { readonly children: ReactNode }) {
  const state = useDesktopSetup();
  const update = useDesktopUpdate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const wasRegistered = useRef(false);
  const registered = state?.status === 'registered';

  useEffect(() => {
    if (registered) wasRegistered.current = true;
    else if (state && wasRegistered.current) window.location.replace('/setup');
  }, [registered, state]);

  if (!state) return <Spinner />;
  if (!registered && pathname !== '/setup') return <Navigate to="/setup" replace />;
  // The office Backup Agent (M7-8) has no work screens and no sign-in: its page, updates, setup.
  if (registered && state.device?.mode === 'BACKUP_AGENT' && !AGENT_PAGES.has(pathname)) {
    return <Navigate to="/backup-agent" replace />;
  }
  if (registered && update?.beforeWork) return <UpdateBeforeWork state={update} />;
  return children;
}
